import { SafeError, SafePromise } from '@benzinga/safe-await';
import { StockSymbol } from '@benzinga/session';
import React from 'react';

interface BatchRequestProp<T, U> {
  serviceCaller: (config: { symbols: StockSymbol[] } & U) => SafePromise<T[]>;
  config: U;
}

export type UseBatchRequestResult<T> = {
  data: T[] | undefined;
  isLoading: boolean;
};

export const useBatchRequest = <T, U extends { symbols?: StockSymbol[] }>(
  props: BatchRequestProp<T, U>,
): UseBatchRequestResult<T> => {
  const { config, serviceCaller } = props;
  const [data, setData] = React.useState<T[]>();
  const [isLoading, setIsLoading] = React.useState(false);
  const previousRequestKeyRef = React.useRef<string | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const requestKey = config?.symbols?.length ? JSON.stringify(config) : null;

  React.useEffect(() => {
    const symbols = config?.symbols;
    if (!symbols?.length) {
      setData(undefined);
      setIsLoading(false);
      previousRequestKeyRef.current = null;
      return;
    }

    const currentRequestKey = JSON.stringify(config);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    previousRequestKeyRef.current = currentRequestKey;
    setData(undefined);
    setIsLoading(true);

    const fetchBatch = async (batchSymbols: StockSymbol[]) => {
      try {
        // serviceCaller must be a bound function when passing through props
        // (e.g. manager.getRatings.bind(manager))
        const response = await serviceCaller.call(serviceCaller, {
          ...config,
          symbols: batchSymbols,
        });
        if (controller.signal.aborted) return { err: 'Request cancelled' };
        if (response.err) return { err: 'No data found' };
        return { ok: response.ok };
      } catch (err) {
        console.error(err);
        return { err };
      }
    };

    const fetchAllBatches = async (): SafePromise<T[]> => {
      try {
        const MAX_BATCH_SIZE = 700;
        const totalSymbols = symbols.length;
        const accumulatedData: T[] = [];
        for (let startIndex = 0; startIndex < totalSymbols; startIndex += MAX_BATCH_SIZE) {
          if (controller.signal.aborted) {
            return { err: new SafeError('Request cancelled', 'Request cancelled') };
          }
          const batchSymbols = symbols.slice(startIndex, startIndex + MAX_BATCH_SIZE);
          const response = await fetchBatch(batchSymbols);
          if (controller.signal.aborted) {
            return { err: new SafeError('Request cancelled', 'Request cancelled') };
          }
          if (response.err || !response.ok) {
            if (!controller.signal.aborted) {
              setIsLoading(false);
            }
            return {
              err: (response.err as SafeError) ?? new SafeError('Batch request failed', 'Batch request failed'),
            };
          }
          accumulatedData.push(...response.ok);
        }
        if (!controller.signal.aborted) {
          setData(accumulatedData);
          setIsLoading(false);
        }
        if (accumulatedData.length === 0) {
          return { err: new SafeError('Nothing found', 'Nothing found') };
        }
        return { ok: accumulatedData };
      } catch (err) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
        return { err: err as SafeError };
      }
    };

    void fetchAllBatches();
    return () => {
      controller.abort();
    };
  }, [requestKey, serviceCaller]);

  const requestChanged = requestKey !== previousRequestKeyRef.current;

  return {
    data: requestChanged ? undefined : data,
    isLoading: requestChanged && requestKey !== null ? true : isLoading,
  };
};
